import { uiText, useUiLanguage, currentLocale } from '@/i18n/interface';

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Receipt, Edit, Trash2 } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { PaymentCategory } from '@/types/payment';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import { useAuth } from '@/contexts/AuthContext';
import { useEffectiveDojo } from '@/hooks/useEffectiveDojo';

interface Expense {
  id: string;
  category_name: string;
  amount: number;
  currency: string;
  description?: string;
  expense_date: string;
  created_at: string;
}

interface ExpenseManagerProps {
  categories: PaymentCategory[];
}

const ExpenseManager: React.FC<ExpenseManagerProps> = ({ categories }) => {
  useUiLanguage();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [expenseForm, setExpenseForm] = useState({
    category_name: '',
    amount: '',
    description: '',
    expense_date: new Date().toISOString().split('T')[0]
  });
  const { toast } = useToast();
  const { profile } = useAuth();
  const { effectiveDojoId, shouldFilterByDojo } = useEffectiveDojo();
  const defaultCurrency = 'CHF'; // Default currency for expenses
  const currencySymbol = getCurrencySymbol(defaultCurrency);

  const fetchExpenses = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('expenses')
        .select('*')
        .order('expense_date', { ascending: false });

      // Filter by dojo if we should (not super_user or in impersonation mode)
      if (shouldFilterByDojo && effectiveDojoId) {
        query = query.eq('dojo_id', effectiveDojoId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setExpenses(data || []);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to load expenses"),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddExpense = async () => {
    if (!expenseForm.category_name || !expenseForm.amount) {
      toast({
        title: uiText("Error"),
        description: uiText("Please fill in all required fields"),
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('expenses')
        .insert({
          category_name: expenseForm.category_name,
          amount: Math.round(parseFloat(expenseForm.amount) * 100), // Convert to cents
          description: expenseForm.description,
          expense_date: expenseForm.expense_date,
          currency: defaultCurrency,
          dojo_id: effectiveDojoId!
        });

      if (error) throw error;

      toast({
        title: uiText("Success"),
        description: uiText("Expense added successfully")
      });

      setExpenseForm({
        category_name: '',
        amount: '',
        description: '',
        expense_date: new Date().toISOString().split('T')[0]
      });
      setIsDialogOpen(false);
      fetchExpenses();
    } catch (error) {
      console.error('Error adding expense:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to add expense"),
        variant: "destructive"
      });
    }
  };

  const handleEditExpense = (expense: Expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      category_name: expense.category_name,
      amount: (expense.amount / 100).toString(),
      description: expense.description || '',
      expense_date: expense.expense_date
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateExpense = async () => {
    if (!editingExpense || !expenseForm.category_name || !expenseForm.amount) {
      toast({
        title: uiText("Error"),
        description: uiText("Please fill in all required fields"),
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('expenses')
        .update({
          category_name: expenseForm.category_name,
          amount: Math.round(parseFloat(expenseForm.amount) * 100),
          description: expenseForm.description,
          expense_date: expenseForm.expense_date
        })
        .eq('id', editingExpense.id);

      if (error) throw error;

      toast({
        title: uiText("Success"),
        description: uiText("Expense updated successfully")
      });

      setExpenseForm({
        category_name: '',
        amount: '',
        description: '',
        expense_date: new Date().toISOString().split('T')[0]
      });
      setIsEditDialogOpen(false);
      setEditingExpense(null);
      fetchExpenses();
    } catch (error) {
      console.error('Error updating expense:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to update expense"),
        variant: "destructive"
      });
    }
  };

  const handleDeleteExpense = async (expenseId: string) => {
    if (!window.confirm(uiText("Are you sure you want to delete this expense?"))) {
      return;
    }

    try {
      const { error } = await supabase
        .from('expenses')
        .delete()
        .eq('id', expenseId);

      if (error) throw error;

      toast({
        title: uiText("Success"),
        description: uiText("Expense deleted successfully")
      });

      fetchExpenses();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to delete expense"),
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              {uiText("Expense Management")}</CardTitle>
            <CardDescription>
              {uiText("Track and manage dojo expenses")}</CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                {uiText("Add Expense")}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{uiText("Add New Expense")}</DialogTitle>
                <DialogDescription>
                  {uiText("Record a new expense for the dojo")}</DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="category">{uiText("Category")}</Label>
                  <Input
                    id="category"
                    value={expenseForm.category_name}
                    onChange={(e) => setExpenseForm({...expenseForm, category_name: e.target.value})}
                    placeholder={uiText("Enter expense category (e.g., Equipment, Utilities, Marketing)")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="amount">{uiText("Amount (")}{currencySymbol})</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date">{uiText("Date")}</Label>
                  <Input
                    id="date"
                    type="date"
                    value={expenseForm.expense_date}
                    onChange={(e) => setExpenseForm({...expenseForm, expense_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">{uiText("Description (Optional)")}</Label>
                  <Textarea
                    id="description"
                    value={expenseForm.description}
                    onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                    placeholder={uiText("Enter expense description...")}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  {uiText("Cancel")}</Button>
                <Button onClick={handleAddExpense}>
                  {uiText("Add Expense")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Edit Expense Dialog */}
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{uiText("Edit Expense")}</DialogTitle>
                <DialogDescription>
                  {uiText("Update the expense details")}</DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-category">{uiText("Category")}</Label>
                  <Input
                    id="edit-category"
                    value={expenseForm.category_name}
                    onChange={(e) => setExpenseForm({...expenseForm, category_name: e.target.value})}
                    placeholder={uiText("Enter expense category (e.g., Equipment, Utilities, Marketing)")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-amount">{uiText("Amount (")}{currencySymbol})</Label>
                  <Input
                    id="edit-amount"
                    type="number"
                    step="0.01"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-date">{uiText("Date")}</Label>
                  <Input
                    id="edit-date"
                    type="date"
                    value={expenseForm.expense_date}
                    onChange={(e) => setExpenseForm({...expenseForm, expense_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">{uiText("Description (Optional)")}</Label>
                  <Textarea
                    id="edit-description"
                    value={expenseForm.description}
                    onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                    placeholder={uiText("Enter expense description...")}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  {uiText("Cancel")}</Button>
                <Button onClick={handleUpdateExpense}>
                  {uiText("Update Expense")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8">{uiText("Loading expenses...")}</div>
        ) : expenses.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {uiText("No expenses recorded yet.")}</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{uiText("Date")}</TableHead>
                  <TableHead>{uiText("Category")}</TableHead>
                  <TableHead>{uiText("Description")}</TableHead>
                  <TableHead>{uiText("Amount")}</TableHead>
                  <TableHead>{uiText("Actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell>{new Date(expense.expense_date).toLocaleDateString(currentLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '.')}</TableCell>
                    <TableCell>{expense.category_name}</TableCell>
                    <TableCell>{expense.description || 'N/A'}</TableCell>
                    <TableCell>{currencySymbol}{(expense.amount / 100).toFixed(2)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditExpense(expense)}
                          title={uiText("Edit Expense")}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteExpense(expense.id)}
                          title={uiText("Delete Expense")}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExpenseManager;
